import { APIClient } from "api/api_helper";
import { GetUserData, CurrentUser } from "common/service/CommonService";
import { IP_API } from "api/url_helper";
import { IpInventoryFormValues } from "modules/Pharmacy/IPinventory/helpers/ipInventory.types";

const api = new APIClient();

export const ipInventoryApiService = {

  // SELECT ALL
  getSelectAllData: (params: any) => {
    return api.create(IP_API.IP_API + "SelectAll", params);
  },

  // SELECT BY ID
  getSelectData: (id: number | string) => {
    return api.create(IP_API.IP_API + "Select", { studyid: id });
  },

  getSelectIpDetailData: (payload: { studyid?: number; ipid?: number; receiptnumber?: string; ipcategory?: string }) => {
    return api.create(IP_API.IP_API + "Select", payload);
  },

  getSelectIpData: (id: number | string) => {
    return api.create(IP_API.IP_API + "IPSelect", { studyid: id });
  },


  verifyPin: (payload: { verifypin: string; ipid?: number | string | null; receiptnumber?: string | null; studyid?: number | string | null; verifiedby: number }) => {
    return api.create(IP_API.IP_API + "Verify", payload);
  },


  // INSERT (Constructs FormData internally)
  insertData: (payload_data: IpInventoryFormValues) => {
    const user = GetUserData() as CurrentUser;

    const formData = new FormData();

    // 1. Append form fields
    Object.entries(payload_data).forEach(([key, value]) => {
      // Skip attachments (handled below) and empty/null values if necessary
      if (key === "attachments" || value === null || value === undefined) return;

      if ((key === "containers" || key === "coadetails") && Array.isArray(value)) {
        formData.append(key, JSON.stringify(value));
        return;
      }

      if (key === "manufacturerlabel" && Array.isArray(value)) {
        value.forEach((item) => {
          formData.append(key, String(item));
        });
        return;
      }

      // Regular fields
      formData.append(key, String(value));
    });

    // 2. Append files
    if (Array.isArray(payload_data.file)) {
      payload_data.file.forEach((file: any) => {
        const typeKey = file.attachmenttype ? `files[${file.attachmenttype}]` : "files";
        formData.append(typeKey, file);
      });
    }

    // 3. System fields
    formData.append("status", "A");
    formData.append("createdby", String(user?.userid ?? ""));


    return api.create(IP_API.IP_API + "Insert", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  // UPDATE (Constructs FormData internally)
  updateData: (payload_data: IpInventoryFormValues & { ipid: number | string; updatereason: string; }) => {
    const user = GetUserData() as CurrentUser;

    const formData = new FormData();

    // 1. Append form fields 
    Object.entries(payload_data).forEach(([key, value]) => {
      // Skip file and empty values but ENSURE updatereason goes through even if empty string
      if (key === "file" || value === null || value === undefined) return;

      // Handle deleted attachments array
      if (key === "deletedAttachmentIds" && Array.isArray(value)) {
        formData.append(key, JSON.stringify(value));
        return;
      }

      if ((key === "containers" || key === "coadetails") && Array.isArray(value)) {
        formData.append(key, JSON.stringify(value));
        return;
      }

      if (key === "manufacturerlabel" && Array.isArray(value)) {
        value.forEach((item) => {
          formData.append(key, String(item));
        });
        return;
      }

      // Regular fields
      formData.append(key, String(value));
    });

    // 2. Append files
    if (Array.isArray(payload_data.file)) {
      payload_data.file.forEach((file: any) => {
        const typeKey = file.attachmenttype ? `files[${file.attachmenttype}]` : "files";
        formData.append(typeKey, file);
      });
    }

    // 3. System fields
    formData.append("status", "A");
    formData.append("updatedby", String(user?.userid ?? ""));


    return api.create(IP_API.IP_API + "Update", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  // DELETE METHOD
  deleteData: (payload_data: { ipid: number | null; receiptnumber: string; studyid: number; updatereason: string; }) => {
    const user = GetUserData() as CurrentUser;
    const payload = {
      ipid: payload_data.ipid,
      studyid: payload_data.studyid,
      receiptnumber: payload_data.receiptnumber,
      updatereason: payload_data.updatereason,
      updatedby: user.userid,
    };
    return api.create(IP_API.IP_API + "Delete", payload);
  },

  // ACCEPTANCE INSERT
  insertAcceptanceData: (payload_data: any) => {
    const user = GetUserData() as CurrentUser;
    const payload = {
      ipid: payload_data.ipid ? Number(payload_data.ipid) : null,
      studyid: payload_data.studyid ? Number(payload_data.studyid) : null,
      receiptnumber: payload_data.receiptnumber || "",
      acceptancedate: payload_data.acceptancedate || null,
      selectedcontainer: payload_data.selectedcontainer || "",
      trainingqty: payload_data.trainingqty ? Number(payload_data.trainingqty) : null,
      physicalverificationqty: payload_data.physicalverificationqty ? Number(payload_data.physicalverificationqty) : null,
      acceptancedetails: {
        comments: payload_data.comments || null,
        ipdescription: payload_data.ipdescription || null,
        assaydifference: payload_data.assaydifference || null,
        primarycontainer: payload_data.primarycontainer || "",
      },
      createdby: user?.userid || 0,
    };
    return api.create(IP_API.ACCEPTANCE_API + "Insert", payload);
  },

  // ACCEPTANCE UPDATE
  updateAcceptanceData: (payload_data: any) => {
    const user = GetUserData() as CurrentUser;
    const payload = {
      acceptanceid: payload_data.acceptanceid ? Number(payload_data.acceptanceid) : null,
      ipid: payload_data.ipid ? Number(payload_data.ipid) : null,
      studyid: payload_data.studyid ? Number(payload_data.studyid) : null,
      receiptnumber: payload_data.receiptnumber || "",
      acceptancedate: payload_data.acceptancedate || null,
      selectedcontainer: payload_data.selectedcontainer || "",
      trainingqty: payload_data.trainingqty ? Number(payload_data.trainingqty) : null,
      physicalverificationqty: payload_data.physicalverificationqty ? Number(payload_data.physicalverificationqty) : null,
      acceptancedetails: {
        comments: payload_data.comments || null,
        ipdescription: payload_data.ipdescription || null,
        assaydifference: payload_data.assaydifference || null,
        primarycontainer: payload_data.primarycontainer || "",
      },
      updatereason: payload_data.updatereason || "",
      updatedby: user?.userid || 0,
    };
    return api.create(IP_API.ACCEPTANCE_API + "Update", payload);
  },
};